import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  MiniMap,
  ReactFlow,
  SelectionMode,
  useReactFlow,
  type OnNodeDrag,
  type OnMoveEnd,
  type OnMove,
  type OnConnectEnd,
  type Viewport,
  type XYPosition,
} from "@xyflow/react";
import { MousePointer2 } from "lucide-react";
import { useEditorStore } from "../../store/editor-store";
import { COMPONENT_DND_MIME } from "../shell/ComponentToolbar";
import { componentRegistry } from "../../core/component-registry";
import { CanvasNode } from "./CanvasNode";
import { CanvasEdge } from "./CanvasEdge";
import { CanvasContextMenu } from "./CanvasContextMenu";
import { useCanvasShortcuts } from "./useCanvasShortcuts";
import { COMPONENT_SNAP_THRESHOLD_PX } from "../../core/node-snapping";
import { CanvasDocumentWindowLayer } from "./CanvasDocumentWindows";
import { useCanvasDocumentWindowStore } from "./document-window-store";
import { CanvasInteractionProvider } from "./CanvasInteractionContext";
import type { SnapGuide } from "../../core/node-snapping";

const nodeTypes = { canvasNode: CanvasNode };
const edgeTypes = { canvasEdge: CanvasEdge };

interface MenuState {
  screen: XYPosition;
  flow: XYPosition;
}

interface BoardCanvasProps {
  snapGap: number;
  defaultNodeRadius: number;
  noteSelectionTooltipEnabled: boolean;
}

export function BoardCanvas({ snapGap, defaultNodeRadius, noteSelectionTooltipEnabled }: BoardCanvasProps) {
  const board = useEditorStore((state) => state.board);
  const nodes = useEditorStore((state) => state.nodes);
  const edges = useEditorStore((state) => state.edges);
  const clipboard = useEditorStore((state) => state.clipboard);
  const alignmentGuides = useEditorStore((state) => state.alignmentGuides);
  const onNodesChange = useEditorStore((state) => state.onNodesChange);
  const onEdgesChange = useEditorStore((state) => state.onEdgesChange);
  const onConnect = useEditorStore((state) => state.onConnect);
  const addNode = useEditorStore((state) => state.addNode);
  const addFiles = useEditorStore((state) => state.addFiles);
  const pasteClipboard = useEditorStore((state) => state.pasteClipboard);
  const beginNodeDrag = useEditorStore((state) => state.beginNodeDrag);
  const endNodeDrag = useEditorStore((state) => state.endNodeDrag);
  const saveViewport = useEditorStore((state) => state.saveViewport);
  const resetDocumentWindows = useCanvasDocumentWindowStore((state) => state.reset);
  const { getZoom, screenToFlowPosition } = useReactFlow();
  const defaultViewport = useMemo(
    () => board?.viewport ?? { x: 0, y: 0, zoom: 1 },
    [board?.id],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportFrame = useRef<number | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 800 });
  const [renderViewport, setRenderViewport] = useState<Viewport>(defaultViewport);
  useCanvasShortcuts();

  useEffect(() => {
    setRenderViewport(defaultViewport);
    resetDocumentWindows();
  }, [board?.id, defaultViewport, resetDocumentWindows]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateSize = () => setContainerSize({ width: element.clientWidth, height: element.clientHeight });
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const renderNodes = useMemo(() => {
    if (nodes.length <= 1_200) return nodes;
    const overscan = 520 / renderViewport.zoom;
    const left = -renderViewport.x / renderViewport.zoom - overscan;
    const top = -renderViewport.y / renderViewport.zoom - overscan;
    const right = (containerSize.width - renderViewport.x) / renderViewport.zoom + overscan;
    const bottom = (containerSize.height - renderViewport.y) / renderViewport.zoom + overscan;
    return nodes.filter((node) => {
      const fallback = componentRegistry.get(node.data.kind)?.defaultSize ?? { width: 240, height: 160 };
      const width = node.measured?.width ?? node.width ?? fallback.width;
      const height = node.measured?.height ?? node.height ?? fallback.height;
      return node.position.x + width >= left && node.position.x <= right && node.position.y + height >= top && node.position.y <= bottom;
    });
  }, [containerSize.height, containerSize.width, nodes, renderViewport]);

  const renderEdges = useMemo(() => {
    if (renderNodes === nodes) return edges;
    const visibleIds = new Set(renderNodes.map((node) => node.id));
    return edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target));
  }, [edges, nodes, renderNodes]);

  const onMoveEnd: OnMoveEnd = useCallback((_, viewport) => saveViewport(viewport), [saveViewport]);
  const onMove: OnMove = useCallback((_, viewport) => {
    if (viewportFrame.current) cancelAnimationFrame(viewportFrame.current);
    viewportFrame.current = requestAnimationFrame(() => setRenderViewport(viewport));
  }, []);

  const onNodeDragStart: OnNodeDrag = useCallback((_, node) => {
    beginNodeDrag(node.id, COMPONENT_SNAP_THRESHOLD_PX / getZoom(), snapGap);
  }, [beginNodeDrag, getZoom, snapGap]);
  const onNodeDragStop: OnNodeDrag = useCallback(() => endNodeDrag(), [endNodeDrag]);

  const onConnectEnd: OnConnectEnd = useCallback((event, connectionState) => {
    if (connectionState.isValid || !connectionState.fromNode) return;
    const point = "changedTouches" in event
      ? event.changedTouches[0]
      : event;
    if (!point) return;
    const targetElement = document.elementFromPoint(point.clientX, point.clientY)
      ?.closest<HTMLElement>(".react-flow__node");
    const targetId = targetElement?.dataset.id;
    const sourceId = connectionState.fromNode.id;
    if (!targetId || targetId === sourceId) return;
    onConnect({
      source: sourceId,
      target: targetId,
      sourceHandle: connectionState.fromHandle?.id ?? null,
      targetHandle: null,
    });
  }, [onConnect]);

  const onDrop = useCallback(async (event: React.DragEvent) => {
    event.preventDefault();
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    if (event.dataTransfer.files.length) {
      await addFiles([...event.dataTransfer.files], position);
      return;
    }
    const type = event.dataTransfer.getData(COMPONENT_DND_MIME);
    if (type) addNode(type, position);
  }, [addFiles, addNode, screenToFlowPosition]);

  const onDoubleClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains("react-flow__pane")) return;
    addNode("note", screenToFlowPosition({ x: event.clientX, y: event.clientY }));
  }, [addNode, screenToFlowPosition]);

  const onContextMenu = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains("react-flow__pane")) return;
    event.preventDefault();
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setMenu({
      screen: { x: Math.min(event.clientX - bounds.left, bounds.width - 190), y: Math.min(event.clientY - bounds.top, bounds.height - 190) },
      flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
    });
  }, [screenToFlowPosition]);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, [contenteditable='true']")) return;
      const files = [...(event.clipboardData?.files ?? [])];
      if (!files.length) return;
      event.preventDefault();
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const position = screenToFlowPosition({ x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 });
      void addFiles(files, position);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addFiles, screenToFlowPosition]);

  if (!board) return null;

  return (
    <div
      ref={containerRef}
      className="canvas-region"
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; }}
      onDrop={onDrop}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onPointerDown={() => menu && setMenu(null)}
    >
      <CanvasInteractionProvider value={{ snapGap, defaultNodeRadius, noteSelectionTooltipEnabled }}>
      <ReactFlow
        key={board.id}
        nodes={renderNodes}
        edges={renderEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onMove={onMove}
        onMoveEnd={onMoveEnd}
        onPaneClick={() => useEditorStore.getState().clearSelection()}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        defaultViewport={defaultViewport}
        minZoom={0.08}
        maxZoom={3.2}
        defaultEdgeOptions={{ type: "canvasEdge" }}
        connectionMode={ConnectionMode.Loose}
        selectionMode={SelectionMode.Partial}
        selectionOnDrag
        panOnScroll
        panOnDrag={[1, 2]}
        zoomOnScroll={false}
        zoomOnPinch
        zoomOnDoubleClick={false}
        multiSelectionKeyCode={["Meta", "Control", "Shift"]}
        selectionKeyCode="Shift"
        panActivationKeyCode="Space"
        deleteKeyCode={null}
        onlyRenderVisibleElements
        elevateNodesOnSelect
        nodesDraggable
        nodesConnectable
        snapToGrid={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.1} />
        <Controls showInteractive={false} position="bottom-left" />
        {nodes.length > 3 && nodes.length <= 1_500 ? (
          <MiniMap
            position="bottom-right"
            pannable
            zoomable
            nodeStrokeWidth={2}
            nodeColor={(node) => {
              const colors: Record<string, string> = { butter: "#efd875", rose: "#eba8ae", mint: "#9dd6b6", sky: "#a9c9ef", lavender: "#cbb7ed", graphite: "#55565c" };
              return colors[String(node.data?.color)] ?? "#d9d7cf";
            }}
          />
        ) : null}
      </ReactFlow>

      <CanvasAlignmentGuides guides={alignmentGuides} viewport={renderViewport} />

      <CanvasDocumentWindowLayer nodes={nodes} viewport={renderViewport} />

      {!nodes.length ? (
        <div className="canvas-empty-hint">
          <div className="canvas-empty-hint__icon"><MousePointer2 size={23} /></div>
          <strong>这是一个空画板</strong>
          <span>从左侧拖入组件，或双击任意位置创建便签</span>
        </div>
      ) : null}
      <div className="canvas-help">
        {nodes.length > 1_200 ? <span className="canvas-lod-badge">{nodes.length.toLocaleString()} 项 · 视口模式</span> : null}
        <kbd>Space</kbd> 拖动画布 <kbd>⇧</kbd> 框选 <kbd>⌘0</kbd> 查看全部
      </div>
      {menu ? (
        <CanvasContextMenu
          screen={menu.screen}
          flow={menu.flow}
          canPaste={Boolean(clipboard)}
          onAdd={addNode}
          onPaste={pasteClipboard}
          onClose={() => setMenu(null)}
        />
      ) : null}
      </CanvasInteractionProvider>
    </div>
  );
}

function CanvasAlignmentGuides({
  guides,
  viewport,
}: {
  guides: readonly SnapGuide[];
  viewport: Viewport;
}) {
  if (!guides.length) return null;
  return (
    <div className="canvas-alignment-guides" aria-hidden="true">
      {guides.map((guide, index) => {
        const crossStart = guide.start * viewport.zoom;
        const crossLength = Math.max(1, (guide.end - guide.start) * viewport.zoom);
        const axisPosition = guide.position * viewport.zoom;
        const style = guide.axis === "x"
          ? {
              left: axisPosition + viewport.x,
              top: crossStart + viewport.y,
              height: crossLength,
            }
          : {
              left: crossStart + viewport.x,
              top: axisPosition + viewport.y,
              width: crossLength,
            };
        return (
          <span
            key={`${guide.axis}-${guide.kind}-${index}`}
            className={`canvas-alignment-guide is-${guide.axis} is-${guide.kind}`}
            style={style}
          />
        );
      })}
    </div>
  );
}
